import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Plus, Edit, UserCheck, User, Building2, Briefcase, AlertCircle } from 'lucide-react';
import { RecordActions } from '../../../components/common/RecordActions';
import { RecordViewDialog } from '../../../components/common/RecordViewDialog';
import { useAuth } from '../../../context/AuthContext';
import { omAPI, controllingAPI } from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Card } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';

const Positions = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('positions');
  const [viewItem, setViewItem] = useState(null);
  const navigate = useNavigate();
  
  const [positions, setPositions] = useState([]);
  const [orgUnits, setOrgUnits] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [controllingAreas, setControllingAreas] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formResetCounter, setFormResetCounter] = useState(0);
  
  // Filters
  const [filterOrgUnit, setFilterOrgUnit] = useState('');
  const [filterPositionStatus, setFilterPositionStatus] = useState('');
  
  // Form state
  const [selectedOrgUnit, setSelectedOrgUnit] = useState('');
  const [selectedJob, setSelectedJob] = useState('');
  const [selectedReportsTo, setSelectedReportsTo] = useState('');
  const [selectedControllingArea, setSelectedControllingArea] = useState('');
  const [selectedCostCenter, setSelectedCostCenter] = useState('');
  const [selectedStaffingPercentage, setSelectedStaffingPercentage] = useState('100');
  const [selectedPositionStatus, setSelectedPositionStatus] = useState('VACANT');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [isChiefPosition, setIsChiefPosition] = useState(false);

  const positionStatuses = [
    { value: 'VACANT', label: 'Vacant', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'OCCUPIED', label: 'Occupied', color: 'bg-green-100 text-green-800' },
    { value: 'PLANNED', label: 'Planned', color: 'bg-orange-100 text-orange-800' },
    { value: 'OBSOLETE', label: 'Obsolete', color: 'bg-gray-100 text-gray-800' },
  ];

  const staffingOptions = [
    { value: '100', label: 'Full Time (100%)' },
    { value: '75', label: 'Part Time (75%)' },
    { value: '50', label: 'Part Time (50%)' },
    { value: '25', label: 'Part Time (25%)' },
    { value: '0', label: 'Placeholder (0%)' },
  ];

  const loadMasterData = useCallback(async () => {
    try {
      const [ouRes, jobRes, caRes] = await Promise.all([
        omAPI.getOrgUnits({ status: 'Active' }),
        omAPI.getJobs({ status: 'Active' }),
        controllingAPI.getControllingAreas()
      ]);
      setOrgUnits(ouRes.data || []);
      setJobs(jobRes.data || []);
      setControllingAreas(caRes.data || []);
    } catch (error) {
      console.error('Failed to load master data:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const params = {};
      if (filterOrgUnit) params.org_unit_id = filterOrgUnit;
      if (filterPositionStatus) params.position_status = filterPositionStatus;
      
      const res = await omAPI.getPositions(params);
      setPositions(res.data || []);
    } catch (error) {
      console.error('Failed to load positions:', error);
      toast.error('Failed to load positions');
    }
  }, [filterOrgUnit, filterPositionStatus]);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadCostCenters = async (controllingAreaId) => {
    if (!controllingAreaId) {
      setCostCenters([]);
      return;
    }
    try {
      const res = await controllingAPI.getCostCenters(controllingAreaId);
      setCostCenters(res.data || []);
    } catch (error) {
      setCostCenters([]);
    }
  };

  const handleControllingAreaChange = (value) => {
    setSelectedControllingArea(value);
    setSelectedCostCenter('');
    loadCostCenters(value === 'none' ? '' : value);
  };

  const resetFormState = () => {
    setSelectedOrgUnit('');
    setSelectedJob('');
    setSelectedReportsTo('');
    setSelectedControllingArea('');
    setSelectedCostCenter('');
    setSelectedStaffingPercentage('100');
    setSelectedPositionStatus('VACANT');
    setSelectedStatus('Active');
    setIsChiefPosition(false);
    setCostCenters([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    // Clean up empty strings
    Object.keys(data).forEach(key => {
      if (data[key] === '' || data[key] === 'none') {
        delete data[key];
      }
    });

    // Convert headcount to number
    if (data.headcount) {
      data.headcount = parseInt(data.headcount, 10);
    }

    // Convert is_chief_position to boolean
    data.is_chief_position = isChiefPosition;

    try {
      if (editingItem) {
        await omAPI.updatePosition(editingItem.position_id, data);
        toast.success('Position updated successfully');
      } else {
        await omAPI.createPosition(data);
        toast.success('Position created successfully');
      }
      
      setIsDialogOpen(false);
      setEditingItem(null);
      resetFormState();
      setFormResetCounter(prev => prev + 1);
      loadData();
      loadMasterData(); // Refresh org units in case chief position changed
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const firstError = detail[0];
        const fieldName = firstError?.loc?.slice(-1)[0] || 'field';
        toast.error(`${fieldName}: ${firstError?.msg || 'Validation error'}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed');
      }
    }
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setSelectedOrgUnit(item.org_unit_id || '');
    setSelectedJob(item.job_id || '');
    setSelectedReportsTo(item.reports_to_position_id || '');
    setSelectedControllingArea(item.controlling_area_id || '');
    setSelectedStaffingPercentage(item.staffing_percentage || '100');
    setSelectedPositionStatus(item.position_status || 'VACANT');
    setSelectedStatus(item.status || 'Active');
    setIsChiefPosition(item.is_chief_position || false);
    
    if (item.controlling_area_id) {
      await loadCostCenters(item.controlling_area_id);
      setSelectedCostCenter(item.cost_center_id || '');
    }
    
    setIsDialogOpen(true);
    setFormResetCounter(prev => prev + 1);
  };

  const getStatusColor = (status) => {
    const found = positionStatuses.find(s => s.value === status);
    return found ? found.color : 'bg-gray-100 text-gray-800';
  };

  // Calculate summary stats
  const stats = {
    total: positions.length,
    vacant: positions.filter(p => p.position_status === 'VACANT').length,
    occupied: positions.filter(p => p.position_status === 'OCCUPIED').length,
    planned: positions.filter(p => p.position_status === 'PLANNED').length,
  };

  return (
    <Layout>
      <div className="p-6" data-testid="positions-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/om')}>Org Management</span>
            <span>/</span>
            <span>Positions</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Positions</h1>
          <p className="text-gray-600 mt-1">Manage positions within organizational units</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg"><UserCheck className="h-5 w-5 text-orange-600" /></div>
              <div>
                <p className="text-sm text-gray-600">Total Positions</p>
                <p className="text-xl font-semibold">{stats.total}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg"><AlertCircle className="h-5 w-5 text-yellow-600" /></div>
              <div>
                <p className="text-sm text-gray-600">Vacant</p>
                <p className="text-xl font-semibold text-yellow-600">{stats.vacant}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg"><User className="h-5 w-5 text-green-600" /></div>
              <div>
                <p className="text-sm text-gray-600">Occupied</p>
                <p className="text-xl font-semibold text-green-600">{stats.occupied}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg"><Building2 className="h-5 w-5 text-orange-600" /></div>
              <div>
                <p className="text-sm text-gray-600">Planned</p>
                <p className="text-xl font-semibold text-orange-600">{stats.planned}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-4 flex gap-4">
          <Select value={filterOrgUnit || "all"} onValueChange={(val) => setFilterOrgUnit(val === "all" ? "" : val)}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Filter by Org Unit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Org Units</SelectItem>
              {orgUnits.map(ou => (
                <SelectItem key={ou.org_unit_id} value={ou.org_unit_id}>{ou.org_unit_id} - {ou.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterPositionStatus || "all"} onValueChange={(val) => setFilterPositionStatus(val === "all" ? "" : val)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {positionStatuses.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Content Card */}
        <Card className="p-0 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-gray-500" />
              <h2 className="font-semibold text-gray-900">Positions</h2>
              <span className="text-sm text-gray-500">({positions.length} shown)</span>
            </div>
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                setEditingItem(null);
                resetFormState();
              }
            }}>
              <DialogTrigger asChild>
                {writable && (<Button size="sm" data-testid="create-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Create Position
                </Button>)}
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingItem ? 'Edit' : 'Create'} Position</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} key={formResetCounter} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="position_id">Position ID *</Label>
                      <Input id="position_id" name="position_id" placeholder="e.g., POS001" maxLength={20} defaultValue={editingItem?.position_id || ''} disabled={!!editingItem} required />
                    </div>
                    <div>
                      <Label htmlFor="name">Position Title *</Label>
                      <Input id="name" name="name" placeholder="e.g., Senior Software Engineer" maxLength={100} defaultValue={editingItem?.name || ''} required />
                    </div>
                    <div>
                      <Label htmlFor="short_name">Short Name</Label>
                      <Input id="short_name" name="short_name" placeholder="e.g., Sr SE" maxLength={20} defaultValue={editingItem?.short_name || ''} />
                    </div>
                    <div>
                      <Label htmlFor="org_unit_id">Org Unit *</Label>
                      <Select value={selectedOrgUnit} onValueChange={setSelectedOrgUnit} required>
                        <SelectTrigger><SelectValue placeholder="Select Org Unit" /></SelectTrigger>
                        <SelectContent>
                          {orgUnits.map(ou => (
                            <SelectItem key={ou.org_unit_id} value={ou.org_unit_id}>{ou.org_unit_id} - {ou.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="org_unit_id" value={selectedOrgUnit} />
                    </div>
                    <div>
                      <Label htmlFor="job_id">Job *</Label>
                      <Select value={selectedJob} onValueChange={setSelectedJob} required>
                        <SelectTrigger><SelectValue placeholder="Select Job" /></SelectTrigger>
                        <SelectContent>
                          {jobs.map(j => (
                            <SelectItem key={j.job_id} value={j.job_id}>{j.job_id} - {j.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="job_id" value={selectedJob} />
                    </div>
                    <div>
                      <Label htmlFor="reports_to_position_id">Reports To</Label>
                      <Select value={selectedReportsTo} onValueChange={setSelectedReportsTo}>
                        <SelectTrigger><SelectValue placeholder="Select Supervisor Position" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {positions.filter(p => p.position_id !== editingItem?.position_id && p.status === 'Active').map(p => (
                            <SelectItem key={p.position_id} value={p.position_id}>{p.position_id} - {p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="reports_to_position_id" value={selectedReportsTo === 'none' ? '' : selectedReportsTo} />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="description">Description</Label>
                      <Input id="description" name="description" placeholder="Position description" maxLength={500} defaultValue={editingItem?.description || ''} />
                    </div>
                    <div>
                      <Label htmlFor="staffing_percentage">Staffing</Label>
                      <Select value={selectedStaffingPercentage} onValueChange={setSelectedStaffingPercentage}>
                        <SelectTrigger><SelectValue placeholder="Select Staffing" /></SelectTrigger>
                        <SelectContent>
                          {staffingOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="staffing_percentage" value={selectedStaffingPercentage} />
                    </div>
                    <div>
                      <Label htmlFor="position_status">Position Status</Label>
                      <Select value={selectedPositionStatus} onValueChange={setSelectedPositionStatus}>
                        <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                        <SelectContent>
                          {positionStatuses.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="position_status" value={selectedPositionStatus} />
                    </div>
                    <div>
                      <Label htmlFor="headcount">Headcount</Label>
                      <Input type="number" id="headcount" name="headcount" min="1" max="100" defaultValue={editingItem?.headcount || 1} />
                    </div>
                    <div>
                      <Label htmlFor="vacancy_start_date">Vacancy Start Date</Label>
                      <Input type="date" id="vacancy_start_date" name="vacancy_start_date" defaultValue={editingItem?.vacancy_start_date || ''} />
                    </div>
                    
                    {/* Cost Center Section */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Cost Assignment (Optional)</h3>
                    </div>
                    <div>
                      <Label htmlFor="controlling_area_id">Controlling Area</Label>
                      <Select value={selectedControllingArea} onValueChange={handleControllingAreaChange}>
                        <SelectTrigger><SelectValue placeholder="Select Controlling Area" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {controllingAreas.filter(ca => ca.status === 'Active').map(ca => (
                            <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>{ca.controlling_area_id} - {ca.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="controlling_area_id" value={selectedControllingArea === 'none' ? '' : selectedControllingArea} />
                    </div>
                    <div>
                      <Label htmlFor="cost_center_id">Cost Center</Label>
                      <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter} disabled={!selectedControllingArea || selectedControllingArea === 'none'}>
                        <SelectTrigger><SelectValue placeholder="Select Cost Center" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- None --</SelectItem>
                          {costCenters.filter(cc => cc.status === 'Active').map(cc => (
                            <SelectItem key={cc.cost_center_id} value={cc.cost_center_id}>{cc.cost_center_id} - {cc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="cost_center_id" value={selectedCostCenter === 'none' ? '' : selectedCostCenter} />
                    </div>

                    {/* Other Settings */}
                    <div className="col-span-2 border-t pt-4 mt-2">
                      <div className="flex items-center gap-2">
                        <input 
                          type="checkbox" 
                          id="is_chief_position" 
                          checked={isChiefPosition} 
                          onChange={(e) => setIsChiefPosition(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <Label htmlFor="is_chief_position" className="cursor-pointer">
                          This is the Chief/Head position of the Org Unit
                        </Label>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="status">Status *</Label>
                      <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Planned">Planned</SelectItem>
                          <SelectItem value="Blocked">Blocked</SelectItem>
                        </SelectContent>
                      </Select>
                      <input type="hidden" name="status" value={selectedStatus} />
                    </div>
                    <div>
                      <Label htmlFor="valid_from">Valid From *</Label>
                      <Input type="date" id="valid_from" name="valid_from" defaultValue={editingItem?.valid_from || new Date().toISOString().split('T')[0]} required />
                    </div>
                    <div>
                      <Label htmlFor="valid_to">Valid To</Label>
                      <Input type="date" id="valid_to" name="valid_to" defaultValue={editingItem?.valid_to || ''} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="submit-btn">{editingItem ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Position</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Org Unit</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Job</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Holder</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Position Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {positions.length > 0 ? (
                  positions.map((item) => (
                    <tr key={item.position_id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 font-mono text-sm font-semibold">
                        {item.position_id}
                        {item.is_chief_position && (
                          <span className="ml-2 px-1.5 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">Chief</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm font-medium">{item.name}</div>
                        {item.reports_to_position_name && (
                          <div className="text-xs text-gray-500">Reports to: {item.reports_to_position_name}</div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 text-sm">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          {item.org_unit_name || item.org_unit_id}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1 text-sm">
                          <Briefcase className="h-4 w-4 text-gray-400" />
                          {item.job_name || item.job_id}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {item.holder_employee_name ? (
                          <div className="flex items-center gap-1 text-sm">
                            <User className="h-4 w-4 text-green-500" />
                            <span>{item.holder_employee_name}</span>
                            <span className="text-xs text-gray-500">({item.holder_employee_id})</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${getStatusColor(item.position_status)}`}>
                          {item.position_status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                          item.status === 'Active' ? 'bg-green-100 text-green-800' :
                          item.status === 'Planned' ? 'bg-orange-100 text-orange-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <RecordActions doctype="positions" onView={() => setViewItem(item)} onEdit={() => handleEdit(item)} />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500">
                      No positions found. Click "Create Position" to add one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default Positions;
